// Select — single-value, native <select> styled to match Input chrome.
// Same label / helper / error contract as Input. Renders <option> children
// passed by the caller.

import {
  forwardRef,
  useId,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";

import { cn } from "./cn";

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, helper, error, className, id, children, ...rest },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const describedById = error || helper ? `${selectId}-desc` : undefined;
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1">
      {label !== undefined && (
        <label
          htmlFor={selectId}
          className="text-sm font-medium text-text-primary"
        >
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        aria-invalid={hasError || undefined}
        aria-describedby={describedById}
        data-error={hasError ? "true" : "false"}
        className={cn(
          "rounded-md border bg-raised px-3 py-2 text-base text-text-primary outline-none",
          "transition-fast ease-out appearance-none",
          "focus-visible:shadow-focus focus-visible:border-teal",
          "disabled:cursor-not-allowed disabled:opacity-50",
          hasError ? "border-ember" : "border-border-default",
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      {hasError ? (
        <p id={describedById} className="text-sm text-ember" role="alert">
          {error}
        </p>
      ) : helper !== undefined ? (
        <p id={describedById} className="text-sm text-text-tertiary">
          {helper}
        </p>
      ) : null}
    </div>
  );
});
